import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { MinutaDocumentModel, MinutaGroup, MinutaRecommendation } from "../../../application/mappers/minuta-document-mapper.js";
import { headerZrNumberOf } from "../../../application/mappers/minuta-document-mapper.js";

/**
 * REPORT DEFINITION: MINUTA_OFFICIAL_V2 (Melhoria 012 — versão definitiva)
 * Modelo Oficial da Minuta de Recomendações do INSPECT IA.
 *
 * Esta especificação SUBSTITUI o modelo anterior (V1) nos pontos
 * conflitantes: a estrutura fixa principal do cabeçalho passa a ser
 *
 *   MINUTA
 *   UNIDADE:
 *   SISTEMA:
 *   NÚMERO DA ZR:
 *   ESPECIFICAÇÃO TÉCNICA DE PADRONIZAÇÃO DE MATERIAL:
 *
 * mantendo Cliente, Projeto, nome do Inspetor, QR Code (um só) e o número de
 * rastreabilidade da Minuta — nenhum desses cinco itens foi removido, só
 * reorganizados ao lado da nova estrutura fixa (ver `buildHeader`).
 */
export class MinutaBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const model: MinutaDocumentModel = snapshot.data as MinutaDocumentModel;

    return {
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [36, 145, 36, 45],
      header: (currentPage: number, pageCount: number) => {
        return this.buildHeader(model, currentPage, pageCount);
      },
      footer: (currentPage: number, pageCount: number) => {
        return {
          margin: [36, 15, 36, 0],
          stack: [
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 0.5, lineColor: '#CBD5E1' }],
              margin: [0, 0, 0, 5]
            },
            {
              columns: [
                { text: 'INSPECT-AI | Minuta de Recomendações', style: 'docFooterText' },
                { text: model.trackingNumber ? `${model.trackingNumber} | REV. ${model.revisionNumber}` : '', style: 'docFooterText', alignment: 'center' },
                { text: `Página ${currentPage} de ${pageCount} | Impresso em: ${this.formatDocumentDate(model.printDate)}`, style: 'docFooterText', alignment: 'right' }
              ]
            }
          ]
        };
      },
      content: this.buildContentArray(model),
      styles: {
        docHeaderBrand: { fontSize: 13, bold: true, color: '#111827' },
        docFooterText: { fontSize: 7.5, color: '#4B5563' },
        
        bannerNumberBox: { fontSize: 8.5, bold: true, color: '#FFFFFF', fillColor: '#111827', alignment: 'center' },
        bannerTitleBox: { fontSize: 8.5, bold: true, color: '#111827', fillColor: '#F3F4F6' },
        
        subBannerText: { fontSize: 7.5, bold: true, color: '#111827', fillColor: '#E5E7EB', margin: [4, 2, 4, 2] },

        tblHeaderCell: { fontSize: 7.5, bold: true, color: '#FFFFFF', fillColor: '#374151', alignment: 'center', margin: [2, 3, 2, 3] },
        tableBody: { fontSize: 7.5, color: '#111827', margin: [2, 2.5, 2, 2.5] },
        tblDataCell: { fontSize: 7.5, color: '#111827', margin: [3, 2.5, 3, 2.5] },
        tblDataCellCenter: { fontSize: 7.5, color: '#111827', alignment: 'center', margin: [3, 2.5, 3, 2.5] },
        
        disciplineHeader: { fontSize: 8, bold: true, color: '#111827', fillColor: '#E5E7EB', margin: [4, 4, 4, 4], alignment: 'center' }
      },
      defaultStyle: {
        fontSize: 8,
        font: 'Roboto'
      }
    };
  }

  // -------------------------------------------------------------
  // REPEATING HEADER — estrutura fixa definitiva (Melhoria 012):
  //   MINUTA / UNIDADE: / SISTEMA: / NÚMERO DA ZR: /
  //   ESPECIFICAÇÃO TÉCNICA DE PADRONIZAÇÃO DE MATERIAL:
  // mantendo Cliente, Projeto, Inspetor, QR (um só) e o número de
  // rastreabilidade — reorganizados no bloco direito, sem duplicar nenhum
  // dado entre os dois blocos.
  // -------------------------------------------------------------
  private buildHeader(model: MinutaDocumentModel, currentPage: number, pageCount: number): Content {
    // Sem endpoint de verificação pública dedicado à Minuta (distinto do RI,
    // que usa /verify-report/:reportId — um reportId não resolveria aqui):
    // o QR aponta para a aplicação com o número de rastreabilidade da
    // própria Minuta, único identificador que o leitor do QR pode usar para
    // localizar o documento.
    const qrUrl = model.trackingNumber
      ? `https://app.inspect-ia.com.br/?minuta=${encodeURIComponent(model.trackingNumber)}`
      : `https://app.inspect-ia.com.br/`;
    const logoContent: any = model.tenantLogo ? { image: model.tenantLogo, fit: [160, 40], margin: [0, 0, 0, 3] } : { text: 'INSPECT IA', fontSize: 20, bold: true, color: '#111827', margin: [0, 0, 0, 3] };

    // ESPECIFICAÇÃO TÉCNICA DE PADRONIZAÇÃO DE MATERIAL: norma de fabricação
    // + SPEC, as duas quando existirem — nunca uma delas "preenchida" com a
    // outra, e "—" só quando NENHUMA das duas estiver cadastrada.
    const standard = model.fabricationStandard && model.fabricationStandard !== 'N/A' ? model.fabricationStandard : '';
    const spec = model.materialSpec && model.materialSpec !== 'N/A' ? model.materialSpec : '';
    const materialStandardDisplay = [standard, spec].filter(Boolean).join(' / ') || '—';
    const systemDisplay = model.systemName && model.systemName !== 'N/A' ? model.systemName : '—';
    const unitAreaDisplay = model.unitArea && model.unitArea !== 'N/A' ? model.unitArea : '—';
    const zrDisplay = headerZrNumberOf(model.groups || []);

    // P0-02 V3: defesa de consistência — impedir PDF misto mesmo se camada anterior falhar.
    // Se o modelo declarar executionType, todas as recomendações devem ser do mesmo tipo.
    // P0-02 V4 Etapa 6: executionType é campo tipado obrigatório no MinutaDocumentModel.
    const declaredType = model.executionType;
    if (declaredType && (declaredType === 'PARADA' || declaredType === 'ROTINA')) {
      for (const group of model.groups || []) {
        for (const rec of group.recommendations || []) {
          const recType = (rec.executionType || '').toString().toUpperCase();
          if (recType && recType !== declaredType) {
            throw new Error(
              `P0-02 SEGREGAÇÃO: Minuta ${declaredType} contém recomendação ${recType} (ZR=${rec.zr || '?'}). ` +
              `O snapshot/model está misto. Corrija a filtragem antes do builder.`
            );
          }
        }
      }
    }

    const fixedFieldRow = (label: string, value: string): TableCell[] => [
      { text: label, fontSize: 7, bold: true, color: '#4B5563', margin: [0, 1, 0, 1] },
      { text: value, fontSize: 7, color: '#111827', margin: [0, 1, 0, 1] }
    ];

    const leftStack: any[] = [
      logoContent,
      { text: 'SISTEMA DE INSPEÇÃO E INTEGRIDADE MECÂNICA', fontSize: 6.5, color: '#6B7280', margin: [0, -2, 0, 5] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 240, y2: 0, lineWidth: 0.5, lineColor: '#D1D5DB' }], margin: [0, 0, 0, 5] },
      // P0-02 V5.1 Falha 1: Título deve identificar claramente MINUTA — PARADA / ROTINA.
      // O executionType vem do MinutaDocumentModel (obrigatório desde P0-02 V4).
      { text: model.executionType ? `MINUTA — ${model.executionType}` : 'MINUTA', fontSize: 13, bold: true, color: '#1F2937', margin: [0, 0, 0, 3] },
      {
        table: {
          widths: ['38%', '62%'],
          body: [
            fixedFieldRow('UNIDADE:', unitAreaDisplay),
            fixedFieldRow('SISTEMA:', systemDisplay),
            fixedFieldRow('NÚMERO DA ZR:', zrDisplay),
            fixedFieldRow('ESPECIFICAÇÃO TÉCNICA DE PADRONIZAÇÃO DE MATERIAL:', materialStandardDisplay),
          ]
        },
        layout: 'noBorders'
      }
    ];

    return {
      table: {
        widths: ['45%', '15%', '40%'],
        body: [
          [
            // Left Block — estrutura fixa definitiva
            { stack: leftStack },
            // Center Block — QR único
            {
              stack: [
                { qr: qrUrl, fit: 80, alignment: 'center' }
              ],
              margin: [0, 2, 0, 0]
            },
            // Right Block — Cliente, Projeto, Inspetor, TAG/Tipo e rastreabilidade (mantidos)
            {
              stack: [
                {
                  table: {
                    widths: ['35%', '65%'],
                    body: [
                      [{ text: 'CLIENTE:', fontSize: 7.5, bold: true, color: '#4B5563', margin: [8, 1, 0, 1] }, { text: model.clientName || '—', fontSize: 7.5, color: '#111827', margin: [0, 1, 0, 1] }],
                      [{ text: 'PROJETO:', fontSize: 7.5, bold: true, color: '#4B5563', margin: [8, 1, 0, 1] }, { text: model.projectName || '—', fontSize: 7.5, color: '#111827', margin: [0, 1, 0, 1] }],
                      [{ text: 'TAG / EQUIPAMENTO:', fontSize: 7.5, bold: true, color: '#4B5563', margin: [8, 1, 0, 1] }, { text: `${model.tag} - ${model.equipmentType}`.toUpperCase() || '—', fontSize: 7.5, color: '#111827', margin: [0, 1, 0, 1] }],
                      [{ text: 'INSPETOR:', fontSize: 7.5, bold: true, color: '#4B5563', margin: [8, 1, 0, 1] }, { text: model.inspectorName || '—', fontSize: 7.5, color: '#111827', margin: [0, 1, 0, 1] }],
                      [{ text: 'Nº DA MINUTA:', fontSize: 7.5, bold: true, color: '#4B5563', margin: [8, 1, 0, 1] }, { text: model.trackingNumber || 'RASCUNHO', fontSize: 7.5, color: '#111827', margin: [0, 1, 0, 1] }],
                      [{ text: 'REVISÃO:', fontSize: 7.5, bold: true, color: '#4B5563', margin: [8, 1, 0, 1] }, { text: model.revisionNumber || '--', fontSize: 7.5, color: '#111827', margin: [0, 1, 0, 1] }],
                      [{ text: 'DATA DA IMPRESSÃO:', fontSize: 7.5, bold: true, color: '#4B5563', margin: [8, 1, 0, 1] }, { text: this.formatDocumentDate(model.printDate), fontSize: 7.5, color: '#111827', margin: [0, 1, 0, 1] }]
                    ]
                  },
                  layout: 'noBorders'
                }
              ],
              margin: [10, 5, 0, 0]
            }
          ]
        ]
      },
      layout: {
        hLineWidth: (i: number, node: any) => (i === node.table.body.length ? 0.5 : 0),
        vLineWidth: (i: number) => (i === 2 ? 0.5 : 0),
        hLineColor: () => '#9CA3AF',
        vLineColor: () => '#D1D5DB',
        paddingLeft: (i: number) => (i === 2 ? 12 : 0),
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 8
      },
      margin: [36, 20, 36, 0]
    };
  }

  private formatDocumentDate(isoString: string | undefined | null): string {
    if (!isoString) return '—';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // -------------------------------------------------------------
  // CONTENT
  // -------------------------------------------------------------
  private buildContentArray(model: MinutaDocumentModel): Content[] {
    const content: Content[] = [];

    // Title Section
    content.push({
      table: {
        widths: ['5%', '95%'],
        body: [
          [
            { text: '01', style: 'bannerNumberBox' },
            { text: '  MINUTA DE RECOMENDAÇÕES', style: 'bannerTitleBox' }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#374151',
        vLineColor: () => '#374151',
        paddingLeft: () => 3,
        paddingRight: () => 3,
        paddingTop: () => 2,
        paddingBottom: () => 2
      },
      margin: [0, 0, 0, 10]
    });

    // Avaliação de Engenharia (dados-fonte do RI): condição, resultado e Vida Residual adotada — só quando existe.
    if (model.assessment) {
      const a = model.assessment;
      const life = a.residualLifeAdoptedMonths === null ? '—' : `${a.residualLifeAdoptedMonths} MESES`;
      const calc = a.residualLifeCalculatedMonths !== null && a.residualLifeCalculatedMonths !== a.residualLifeAdoptedMonths ? ` (AVR: ${a.residualLifeCalculatedMonths} meses)` : '';
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              { text: 'CONDIÇÃO DO EQUIPAMENTO', style: 'tblHeaderCell' },
              { text: 'RESULTADO', style: 'tblHeaderCell' },
              { text: 'VIDA RESIDUAL ADOTADA — MESES', style: 'tblHeaderCell' },
              { text: 'ORIGEM DA VIDA RESIDUAL', style: 'tblHeaderCell' }
            ],
            [
              { text: a.equipmentCondition || '—', style: 'tblDataCellCenter', bold: true },
              { text: a.equipmentResult || '—', style: 'tblDataCellCenter', bold: true },
              { text: `${life}${calc}`, style: 'tblDataCellCenter' },
              { text: [a.residualLifeOrigin, a.residualLifeSource].filter(Boolean).join(' · ') || '—', style: 'tblDataCellCenter' }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#D1D5DB',
          vLineColor: () => '#D1D5DB'
        },
        margin: [0, 0, 0, 10]
      });
    }

    if (!model.groups || model.groups.length === 0) {
      content.push({
        text: 'Nenhuma recomendação técnica foi identificada para este contexto.',
        fontSize: 8,
        color: '#4B5563',
        italics: true,
        alignment: 'center',
        margin: [0, 20, 0, 0]
      });
      return content;
    }

    model.groups.forEach(group => {
      // Discipline Header
      content.push({
        table: {
          widths: ['*'],
          body: [
            [{ text: `DISCIPLINA: ${group.discipline.toUpperCase()}`, style: 'disciplineHeader' }]
          ]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#D1D5DB',
          vLineColor: () => '#D1D5DB'
        },
        margin: [0, 5, 0, 0],
        unbreakable: true
      });

      // Recommendations Table for this discipline
      const tableBody: TableCell[][] = [
        [
          { text: 'ITEM', style: 'tblHeaderCell' },
          { text: 'DESCRIÇÃO DA RECOMENDAÇÃO', style: 'tblHeaderCell' }
        ]
      ];

      group.recommendations.forEach(rec => {
        const titleText = rec.title || '-';
        
        // Col 1: Item and ZR
        const itemStack: any[] = [
          { text: rec.line, style: 'tblDataCellCenter', bold: true, fontSize: 10 }
        ];
        
        itemStack.push({
          text: `ZR: ${rec.zr || '—'}`,
          style: 'tblDataCellCenter',
          fontSize: 7,
          color: '#4B5563',
          margin: [0, 8, 0, 0]
        });

        // Col 2: Description and structured data
        const descStack: any[] = [{ text: titleText, style: 'tblDataCell', bold: true, color: '#111827' }];
        
        if (rec.body && rec.body.trim().length > 0) {
           descStack.push({
             text: rec.body,
             fontSize: 8,
             margin: [0, 6, 0, 0]
           });
        }
        
        const dataEntries = [
           { label: 'COMPONENTE', value: rec.componentName },
           { label: 'LOCALIZAÇÃO', value: rec.location },
           { label: 'EXECUÇÃO', value: rec.executionType },
           { label: 'CRITICIDADE', value: rec.criticality === 'MODERATE' ? 'MÉDIA' : rec.criticality === 'LOW' ? 'BAIXA' : rec.criticality === 'HIGH' ? 'ALTA' : rec.criticality === 'CRITICAL' ? 'CRÍTICA' : rec.criticality },
           { label: 'ESCOPO', value: rec.scope },
           { label: 'DIMENSÃO', value: rec.dimensions },
           { label: 'NORMA DE FABRICAÇÃO', value: rec.standard },
           { label: 'PROCEDIMENTO', value: rec.procedure },
           { label: 'OBSERVAÇÃO', value: rec.observation || rec.notes }
        ].filter(e => e.value);

        if (dataEntries.length > 0) {
           const inlineData = dataEntries.map(e => ({ text: `${e.label}: `, bold: true, color: '#4B5563' })).map((e, i) => {
              return [e, { text: `${dataEntries[i]?.value || ''}    `, color: '#111827' }];
           }).flat();
           
           descStack.push({
             text: inlineData,
             fontSize: 7,
             margin: [0, 6, 0, 0]
           });
        }

        tableBody.push([
          { stack: itemStack, alignment: 'center' },
          { stack: descStack }
        ]);
      });

      content.push({
        table: {
          widths: ['15%', '85%'],
          body: tableBody
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0,
          hLineColor: () => '#D1D5DB',
          vLineColor: () => '#D1D5DB',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 6,
          paddingBottom: () => 6
        },
        margin: [0, 0, 0, 15]
      });
    });

    if (model.approval) {
      content.push(this.buildApprovalBlock(model));
    }

    return content;
  }

  // -------------------------------------------------------------
  // APROVAÇÃO / ASSINATURA ELETRÔNICA DO PLH
  // -------------------------------------------------------------
  private buildApprovalBlock(model: MinutaDocumentModel): Content {
    const approval = model.approval!;

    // No forced page break before this block (would risk a blank/near-empty
    // page, forbidden by the PDF rules) — `unbreakable` only keeps the
    // block itself together, it never pushes content to a new page on its
    // own.
    const signatureStack: any[] = [];
    if (approval.signatureImage) {
      // Fixed box so the image never distorts (pdfmake `fit` preserves
      // aspect ratio inside the box) and never grows large enough to
      // invade the name/registration lines below it.
      signatureStack.push({ image: approval.signatureImage, fit: [160, 55], alignment: 'center', margin: [0, 4, 0, 2] });
    } else {
      signatureStack.push({ text: '\n', margin: [0, 20, 0, 0] });
    }
    signatureStack.push({ canvas: [{ type: 'line', x1: 60, y1: 0, x2: 300, y2: 0, lineWidth: 0.75, lineColor: '#374151' }], alignment: 'center', margin: [0, 0, 0, 4] });
    signatureStack.push({ text: approval.plhName.toUpperCase(), fontSize: 9.5, bold: true, color: '#111827', alignment: 'center' });
    signatureStack.push({ text: approval.functionTitle, fontSize: 8, color: '#4B5563', alignment: 'center', margin: [0, 1, 0, 0] });
    if (approval.registration) {
      signatureStack.push({ text: approval.registration, fontSize: 8, color: '#4B5563', alignment: 'center', margin: [0, 1, 0, 0] });
    }

    return {
      unbreakable: true,
      margin: [0, 10, 0, 0],
      table: {
        widths: ['*'],
        body: [
          [{ text: 'APROVADO ELETRONICAMENTE PELO PLH  |  ASSINATURA ELETRÔNICA — INSPECT IA', style: 'disciplineHeader' }],
          [
            {
              margin: [0, 10, 0, 10],
              table: {
                widths: ['50%', '50%'],
                body: [
                  [
                    { stack: signatureStack },
                    {
                      stack: [
                        { text: `Nº DA MINUTA: ${model.trackingNumber || '—'}`, fontSize: 8, color: '#111827', margin: [0, 2, 0, 0] },
                        { text: `REVISÃO: ${model.revisionNumber || '—'}`, fontSize: 8, color: '#111827', margin: [0, 2, 0, 0] },
                        { text: `APROVADO EM: ${this.formatDocumentDateTime(approval.approvedAt)}`, fontSize: 8, color: '#111827', margin: [0, 2, 0, 0] },
                        { text: `VALIDAÇÃO INSPECT IA: ${approval.hashShort || '—'}`, fontSize: 8, color: '#4B5563', margin: [0, 2, 0, 0] }
                      ],
                      margin: [10, 6, 0, 0]
                    }
                  ]
                ]
              },
              layout: 'noBorders'
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#374151',
        vLineColor: () => '#374151'
      }
    };
  }

  private formatDocumentDateTime(isoString: string | undefined | null): string {
    if (!isoString) return '—';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    const datePart = this.formatDocumentDate(isoString);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${datePart} ${hh}:${mm}`;
  }
}

documentEngine.registerBuilder("MINUTA", new MinutaBuilder());
